import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {removeSiteLocaleFromPath} from '../../../utils/url'
import {messages} from '../constant'

/**
 * @param {string} appPath — pathname with site/locale stripped (e.g. `/account/wishlist`)
 * @returns {keyof typeof messages | null}
 */
const getAccountSubPageMessageKey = (appPath) => {
    if (!appPath.startsWith('/account')) {
        return null
    }
    if (appPath === '/account') {
        return null
    }
    if (appPath.startsWith('/account/orders')) {
        return 'orders'
    }
    if (appPath.startsWith('/account/email')) {
        return 'email'
    }
    const parts = appPath.split('/').filter(Boolean)
    const segment = parts[1]
    const segmentToKey = {
        personal: 'profile',
        wishlist: 'wishlist',
        addresses: 'addresses',
        payment: 'payment',
        password: 'password'
    }
    return segmentToKey[segment] ?? null
}

export const AccountBreadcrumb = ({pathname}) => {
    const {formatMessage} = useIntl()
    const breadcrumbStyles = useMultiStyleConfig('Breadcrumb', {})

    const items = useMemo(() => {
        const appPath = removeSiteLocaleFromPath(pathname)
        const accountLabel = formatMessage({
            id: 'account.breadcrumb.root',
            defaultMessage: 'My Account'
        })
        const subKey = getAccountSubPageMessageKey(appPath)
        if (!subKey) {
            return [{label: accountLabel, to: '/account', isCurrentPage: true}]
        }
        return [
            {label: accountLabel, to: '/account', isCurrentPage: false},
            {label: formatMessage(messages[subKey]), to: null, isCurrentPage: true}
        ]
    }, [pathname, formatMessage])

    return (
        <Breadcrumb
            className="sf-breadcrumb"
            data-testid="account-breadcrumb"
            separator="/"
            sx={{
                ...breadcrumbStyles.container,
                maxW: {base: 'full', lg: 'container.xxxl'},
                mx: 'auto',
                mb: {base: 3, lg: 4},
                px: {base: '16px', lg: '24px'},
                pt: {base: 4, lg: 6}
            }}
        >
            {items.map((crumb, index) => (
                <BreadcrumbItem
                    key={`${crumb.label}-${index}`}
                    isCurrentPage={crumb.isCurrentPage}
                    data-testid="account-breadcrumb-item"
                >
                    {crumb.isCurrentPage ? (
                        <Box
                            as="span"
                            sx={{
                                ...breadcrumbStyles.link,
                                ...breadcrumbStyles.lastLink,
                                ...breadcrumbStyles.bcText,
                                ...breadcrumbStyles.bcTextLast
                            }}
                        >
                            {crumb.label}
                        </Box>
                    ) : (
                        <BreadcrumbLink
                            as={Link}
                            to={crumb.to}
                            useNavLink
                            sx={{
                                ...breadcrumbStyles.link,
                                ...breadcrumbStyles.bcText
                            }}
                        >
                            {crumb.label}
                        </BreadcrumbLink>
                    )}
                </BreadcrumbItem>
            ))}
        </Breadcrumb>
    )
}

AccountBreadcrumb.propTypes = {
    pathname: PropTypes.string.isRequired
}
